import type {
  ClientFilter,
  ClientsMessageRequest,
  ClientsMessageResponse,
  ClientsResponse,
  DeskAction,
  FamilyGroup,
  OwnerParticipant,
} from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  CheckIcon,
  ChipRow,
  CloseIcon,
  DataTable,
  EmptyState,
  ErrorState,
  InlineError,
  LockIcon,
  Notice,
  PageHeader,
  PlusCircleIcon,
  SearchField,
  Select,
  Sheet,
  SkeletonList,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useClients, useExportClients, useMessageClients } from "@teamna/web-client/hooks/use-clients.ts";
import { useFamilies, useInviteLog } from "@teamna/web-client/hooks/use-families.ts";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { useParticipants } from "@teamna/web-client/hooks/use-setup.ts";
import { downloadText } from "../download.ts";
import { runsBusiness } from "../office.tsx";
import { WhatsAppSyncButton } from "../whatsapp-sync/sync-tool.tsx";
import { useFamilyActions, useFamilyWords } from "./actions.ts";
import { AddFamiliesPanel, type AddSource } from "./add.tsx";
import { ChildTag, FamilyNameCell, InviteBadge } from "./bits.tsx";
import { ClientsList, clientTableRows } from "./clients.tsx";
import { InvitesView } from "./invites.tsx";
import { capital, householdWord, isPlaced, lastSittingText, searchFamilies, type Words } from "./model.ts";
import { buildParentRows, parentFilterTest, parentsCsv, type ParentFilter, type ParentRow } from "./parents-model.ts";
import type { FamiliesData, FamilyActions } from "./types.ts";

/** What the Clients table needs from the route (C1). Plain values and functions, so Storybook passes them. */
export type ClientsTableProps = {
  response: ClientsResponse | null;
  loading: boolean;
  error: unknown;
  onRetry?: () => void;
  filter: ClientFilter;
  onFilter: (filter: ClientFilter) => void;
  onExport: () => void;
  exporting?: boolean;
  /** Missing: no message to many (the front desk, T1). */
  onMessage?: (input: ClientsMessageRequest) => Promise<ClientsMessageResponse>;
};

/**
 * The Parents page (owner menu revamp, 2026-10-09). Every family in one table: the adults and
 * their numbers, the children with their group, the invite, the sessions left and paid or owed. A
 * row opens the family's own page, where the owner sees and changes everything about it.
 *
 * It replaces the Families page and the members table. Their parts live on: "Add families" (paste,
 * spreadsheet, one family), the welcome card, the filters, the group filter and the export are
 * here; the invites open from the "Not joined yet" filter; the participants list and the groups
 * board are the participants page.
 *
 * The title is the business's member word ("Parents", "Clients").
 */
export function ParentsView({
  data,
  participants,
  actions,
  onOpenParent,
  onOpenCsv,
  onOpenBoard,
  syncAction,
  clients,
  may = () => true,
}: {
  data: FamiliesData;
  participants: OwnerParticipant[];
  actions: FamilyActions;
  onOpenParent: (familyId: string) => void;
  onOpenCsv: () => void;
  /** The groups board on the participants page, for the "without a group" filter. */
  onOpenBoard?: () => void;
  /** "Sync from WhatsApp", when this person may use it. */
  syncAction?: ReactNode;
  /**
   * The Clients table (C1), where clients book for themselves: the server's rows and counts for the
   * filter, the export and the message to many. Without it the page shows the families table.
   */
  clients?: ClientsTableProps;
  /**
   * The jobs this person's role may do (`useActiveBusiness().may`): the front desk (T1) does not add
   * families, send invites or export. Missing: every job, as for the owner (Storybook).
   */
  may?: (action: DeskAction) => boolean;
}) {
  const { families, groups, words } = data;
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ParentFilter>("all");
  /** "all", "none" (a child with no group), or a group id. */
  const [group, setGroup] = useState("all");
  const [error, setError] = useState<unknown>(null);
  const [add, setAdd] = useState<{ open: boolean; source: AddSource; key: number }>({ open: false, source: "paste", key: 0 });
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [welcome, setWelcome] = useState(true);

  const rows = useMemo(() => buildParentRows(families, participants), [families, participants]);
  const found = useMemo(
    () =>
      searchFamilies(
        rows
          .filter(parentFilterTest(filter, groups))
          .filter((row) =>
            group === "all"
              ? true
              : group === "none"
                ? row.family.children.some((child) => !isPlaced(child, groups))
                : row.family.children.some((child) => child.groupId === group),
          ),
        query,
        (row) => row.family,
      ),
    [rows, filter, groups, query, group],
  );
  // Where clients book for themselves the rows come from the Clients list of the server (C1).
  const selfMode = !words.forChildren && clients !== undefined;
  const clientRows = useMemo(
    () => searchFamilies(clientTableRows(clients?.response?.items ?? [], families), query, (row) => row.family),
    [clients?.response, families, query],
  );
  const shown = found.rows;
  useTrackNameSearch("families_parents", query, selfMode ? clientRows.results : found.results, selfMode ? clientRows.rows.length : shown.length);

  const filters: Array<{ id: ParentFilter; label: string }> = [
    { id: "all", label: "All" },
    { id: "owes", label: "Owes money" },
    { id: "low", label: "2 sessions or less" },
    { id: "notjoined", label: "Not joined yet" },
    { id: "nogroup", label: `${capital(words.participant)} without a ${words.group}` },
    { id: "nophone", label: "No number" },
    { id: "onhold", label: "On hold" },
  ];
  const chips = filters
    .map((row) => ({ id: row.id, label: row.label, count: rows.filter(parentFilterTest(row.id, groups)).length }))
    .filter((chip) => chip.id === "all" || chip.id === filter || chip.count > 0);
  const notInvited = families.filter((family) => family.invite === "none").length;
  const children = families.flatMap((family) => family.children);
  // Where clients book for themselves there is no one to put in a group (F2).
  const noGroup = words.forChildren ? children.filter((child) => !isPlaced(child, groups) && child.placementStatus !== "unsure").length : 0;
  const sitting = data.lastSitting ? lastSittingText(data.lastSitting, data.timezone, words) : null;
  const showWelcome = welcome && filter === "all" && !query && families.length > 0 && (sitting !== null || noGroup > 0);

  function openAdd(source: AddSource = "paste") {
    setAdd((current) => ({ open: true, source, key: current.key + 1 }));
  }

  function exportList() {
    if (selfMode) {
      clients.onExport();
      return;
    }
    downloadText(`${words.members}.csv`, parentsCsv(shown, groups, words));
    showToast(`${shown.length} ${shown.length === 1 ? "row" : "rows"} exported`);
  }

  const columns = parentColumns(groups, words);

  return (
    <div className="max-w-[1200px]">
      <PageHeader
        title={capital(words.members)}
        description={
          words.forChildren
            ? `Every family with their ${words.participants}, sessions and payments. Open a ${words.member} to see and change all of it.`
            : `Every ${words.member} with their sessions and payments. Open a ${words.member} to see and change all of it.`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {syncAction}
            {selfMode && notInvited > 0 && may("sendInvites") ? (
              <Button variant="ghost" onClick={() => setInvitesOpen(true)}>
                Send invites ({notInvited})
              </Button>
            ) : null}
            {may("exportClients") ? (
              <Button
                variant="ghost"
                onClick={exportList}
                loading={selfMode ? clients.exporting : undefined}
                disabled={selfMode ? (clients.response?.items.length ?? 0) === 0 : shown.length === 0}
              >
                Export
              </Button>
            ) : null}
            {may("addFamilies") ? (
              <Button onClick={() => openAdd()} className="gap-2">
                <PlusCircleIcon size={18} />
                Add {words.members}
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? (
        <div className="relative mb-4">
          <InlineError error={error} />
          <button type="button" onClick={() => setError(null)} aria-label="Hide this message" className="absolute top-3 right-3 text-ink-2">
            <CloseIcon size={16} />
          </button>
        </div>
      ) : null}

      <section aria-label={capital(words.members)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <SearchField
              label={`Search ${words.members}`}
              value={query}
              onChange={setQuery}
              placeholder={words.forChildren ? `Search a ${words.member}, a ${words.participant} or a number` : `Search a ${words.member} or a number`}
            />
          </div>
          {/* The filter reads the group of each child: a client who books for herself has none (F2). */}
          {groups.length > 0 && words.forChildren ? (
            <Select label={`Filter by ${words.group}`} value={group} onChange={(event) => setGroup(event.target.value)} className="sm:w-[220px]">
              <option value="all">Every {words.group}</option>
              {groups.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
              <option value="none">No {words.group} yet</option>
            </Select>
          ) : null}
        </div>
        {showWelcome ? (
          <section aria-label="Where you left off" className="rounded-card bg-paper p-4 shadow-card ring-1 ring-line">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-[17px]">Welcome back. Here is where you left off.</h2>
                {sitting ? <p className="mt-1 text-[14px] text-ink-2">{sitting}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => setWelcome(false)}
                aria-label="Hide this card"
                className="flex size-9 items-center justify-center rounded-lg text-ink-2 hover:bg-sunken"
              >
                <CloseIcon size={16} />
              </button>
            </div>
            {noGroup > 0 ? (
              <div className="notice notice-warning mt-3 flex flex-col items-start gap-3 rounded-xl px-3.5 py-2.5 sm:flex-row sm:items-center">
                <span className="min-w-0 flex-1 text-[14px] font-semibold">
                  {noGroup} {noGroup === 1 ? `${words.participant} has` : `${words.participants} have`} no {words.group} yet. Their{" "}
                  {words.members} would see no sessions for them.
                </span>
                {onOpenBoard ? (
                  <Button variant="secondary" onClick={onOpenBoard}>
                    Put them in {words.groups}
                  </Button>
                ) : null}
              </div>
            ) : words.forChildren ? (
              <p className="mt-3 flex items-center gap-2 text-[14px] font-semibold text-confirmed-ink">
                <CheckIcon size={16} /> Every {words.participant} has a {words.group}.
              </p>
            ) : null}
          </section>
        ) : null}
        {selfMode ? null : <ChipRow options={chips} selectedId={filter} onSelect={(id) => setFilter(id as ParentFilter)} label="Show" wrap />}

        {filter === "notjoined" && notInvited > 0 && may("sendInvites") ? (
          <Notice
            tone="info"
            title={`${notInvited} ${notInvited === 1 ? `${words.member} is` : `${words.members} are`} not invited yet`}
            action={
              <div>
                <Button variant="secondary" onClick={() => setInvitesOpen(true)}>
                  Send invites
                </Button>
              </div>
            }
          >
            Send the invite when their {words.participants} have a {words.group}. No one gets a message until you send it.
          </Notice>
        ) : null}
        {filter === "nogroup" && onOpenBoard ? (
          <Notice
            tone="warning"
            title={`Put each ${words.participant} in a ${words.group}`}
            action={
              <div>
                <Button variant="secondary" onClick={onOpenBoard}>
                  Open the {words.groups} board
                </Button>
              </div>
            }
          >
            Their {words.members} see no sessions for them until then.
          </Notice>
        ) : null}

        {rows.length === 0 ? (
          <EmptyState
            title={`No ${words.members} yet`}
            message={
              may("addFamilies")
                ? `Add your ${words.members} with "Add ${words.members}". Paste a list, use a spreadsheet, or type one ${householdWord(1, words)}.`
                : `The owner adds the ${words.members}.`
            }
            {...(may("addFamilies")
              ? {
                  action: (
                    <Button onClick={() => openAdd()} className="gap-2">
                      <PlusCircleIcon size={18} />
                      Add {words.members}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : selfMode ? (
          <ClientsList
            response={clients.response}
            rows={clientRows.rows}
            loading={clients.loading}
            error={clients.error}
            onRetry={clients.onRetry}
            filter={clients.filter}
            onFilter={clients.onFilter}
            searched={clientRows.searched}
            ranked={clientRows.results.length > 0}
            timezone={data.timezone}
            words={words}
            onOpenClient={onOpenParent}
            {...(clients.onMessage ? { onMessage: clients.onMessage } : {})}
          />
        ) : (
          <DataTable
            caption={capital(words.members)}
            columns={columns}
            rows={shown}
            rowKey={(row) => row.family.id}
            rowLabel={(row) => row.name}
            onRowClick={(row) => onOpenParent(row.family.id)}
            defaultSort={{ id: "name", direction: "asc" }}
            ranked={found.results.length > 0}
            countLabel={(count) => `${count} ${count === 1 ? words.member : words.members}`}
            empty={{
              title: found.searched ? `No ${words.member} matches` : "Nothing here",
              message: found.searched ? "Try a name or the last digits of a number." : `No ${householdWord(1, words)} is in this list.`,
            }}
          />
        )}
      </section>

      <AddFamiliesPanel
        key={add.key}
        open={add.open}
        onClose={() => setAdd((current) => ({ ...current, open: false }))}
        groups={groups}
        words={words}
        actions={actions}
        initialSource={add.source}
        onOpenCsv={onOpenCsv}
        onError={setError}
      />
      <Sheet open={invitesOpen} onClose={() => setInvitesOpen(false)} title="Send invites">
        <InvitesView
          families={families}
          groups={groups}
          words={words}
          log={data.inviteLog}
          timezone={data.timezone}
          actions={actions}
          onError={setError}
        />
      </Sheet>
    </div>
  );
}

/** The table's columns, in the business's words. */
function parentColumns(groups: FamilyGroup[], words: Words): DataTableColumn<ParentRow>[] {
  const columns: DataTableColumn<ParentRow>[] = [
    {
      id: "name",
      header: capital(words.member),
      sortValue: (row) => row.name,
      cell: (row) => <FamilyNameCell family={row.family} name={row.name} />,
    },
    {
      id: "children",
      header: capital(words.participants),
      cell: (row) =>
        row.family.children.length ? (
          <span className="flex min-w-[180px] flex-wrap gap-1.5">
            {row.family.children.map((child) => (
              <ChildTag key={child.participantId} child={child} groups={groups} words={words} />
            ))}
          </span>
        ) : (
          <span className="text-[13px] text-ink-2">None yet</span>
        ),
    },
    {
      id: "invite",
      header: "Status",
      hideOnPhone: true,
      sortValue: (row) => ({ joined: 0, invited: 1, none: 2 })[row.family.invite],
      cell: (row) => <InviteBadge state={row.family.invite} />,
    },
    {
      id: "sessions",
      header: "Sessions left",
      align: "end",
      sortValue: (row) => row.sessionsLeft,
      cell: (row) =>
        row.notReady ? (
          <span className="text-[13px] text-ink-3">Not ready</span>
        ) : row.sessionsLeft === null ? (
          <span className="text-[13px] text-ink-3">No package</span>
        ) : row.unlimited ? (
          <span className="font-semibold text-ink">No limit</span>
        ) : (
          <span className={`font-semibold tabular-nums ${row.low ? "text-late" : "text-ink"}`}>{row.sessionsLeft}</span>
        ),
    },
    {
      id: "payment",
      header: "Payment",
      sortValue: (row) => (row.payment === "owed" ? 0 : row.payment === "paid" ? 1 : 2),
      cell: (row) =>
        row.notReady ? (
          <span className="text-[13px] text-ink-3">Not ready</span>
        ) : row.payment === "owed" ? (
          <Badge tone="late">Owes</Badge>
        ) : row.payment === "paid" ? (
          <Badge tone="confirmed">Paid</Badge>
        ) : (
          <span className="text-[13px] text-ink-3">Nothing yet</span>
        ),
    },
  ];
  // Where clients book for themselves the client is the participant (F2): no column of children.
  return words.forChildren ? columns : columns.filter((column) => column.id !== "children");
}

/** The route: reads the families and the participants, wires each action to the API. */
export function OwnerParentsScreen() {
  const { business, isLoading, error } = useActiveBusiness();
  if (isLoading && !business) return <SkeletonList />;
  if (error && !business) return <ErrorState error={error} />;
  if (!business) {
    return <EmptyState icon={<LockIcon size={30} />} title="No business to manage" message="This account does not run a business yet." />;
  }
  return (
    <ParentsBody
      businessId={business.businessId}
      timezone={business.timezone}
      businessName={business.name}
      canSync={runsBusiness(business)}
    />
  );
}

function ParentsBody({
  businessId,
  timezone,
  businessName,
  canSync,
}: {
  businessId: string;
  timezone: string;
  businessName: string;
  canSync: boolean;
}) {
  const navigate = useNavigate();
  const { may } = useActiveBusiness();
  const words = useFamilyWords();
  const families = useFamilies(businessId);
  const participants = useParticipants(businessId);
  const log = useInviteLog(may("sendInvites") ? businessId : null);
  const actions = useFamilyActions(businessId);
  // The Clients table (C1): only where clients book for themselves.
  const [clientFilter, setClientFilter] = useState<ClientFilter>("all");
  const clientList = useClients(words.forChildren ? null : businessId, clientFilter);
  const exportClients = useExportClients(businessId);
  const messageClients = useMessageClients(businessId);
  const clients: ClientsTableProps | undefined = words.forChildren
    ? undefined
    : {
        response: clientList.data ?? null,
        loading: clientList.isFetching,
        error: clientList.error,
        onRetry: () => void clientList.refetch(),
        filter: clientFilter,
        onFilter: (next) => {
          setClientFilter(next);
          track("client_list_filtered", { filter: next });
        },
        onExport: () =>
          exportClients.mutate(
            { filter: clientFilter },
            {
              onSuccess: (text) => {
                downloadText(`${words.members}-${clientFilter.replace(/_/g, "-")}.csv`, text);
                const count = clientList.data?.items.length ?? 0;
                showToast(`${count} ${count === 1 ? "row" : "rows"} exported`);
              },
              onError: () => showToast("The export did not work. Try again."),
            },
          ),
        exporting: exportClients.isPending,
        ...(may("messageClients") ? { onMessage: (input: ClientsMessageRequest) => messageClients.mutateAsync(input) } : {}),
      };

  // The participants read carries the balances and paid or owed. Without it the table would say
  // "No package" and "Nothing yet" for every family, so it waits for it or says the read failed.
  if (families.isLoading || (participants.isLoading && !participants.data)) return <SkeletonList />;
  if (families.error || !families.data) return <ErrorState error={families.error} onRetry={() => void families.refetch()} />;
  if (participants.error && !participants.data) return <ErrorState error={participants.error} onRetry={() => void participants.refetch()} />;

  return (
    <ParentsView
      data={{ ...families.data, inviteLog: log.data ?? null, words, timezone, businessName }}
      participants={participants.data ?? []}
      actions={actions}
      onOpenParent={(id) => void navigate({ to: "/owner/parents/$id", params: { id } })}
      onOpenCsv={() => void navigate({ to: "/owner/setup/import/csv" })}
      {...(may("placeParticipant") ? { onOpenBoard: () => void navigate({ to: "/owner/participants/board" }) } : {})}
      syncAction={canSync ? <WhatsAppSyncButton businessId={businessId} /> : undefined}
      clients={clients}
      may={may}
    />
  );
}
